import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { OrganizationMutators } from '~/mutators/organizations'
import { SetupWizard } from '~/templates/wizard/presets/setup'

import { Button, Heading1, Icon, type IconTypes, cn } from '@hap/components'
import { CALL_PROFILE_LABELS, type CallProfile } from '@hap/constants'

export const Route = createFileRoute('/_authenticated/$alias/setup/customize')({
  component: RouteComponent,
})

const PROFILE_ICONS: Record<CallProfile, IconTypes> = {
  customer_service: 'hand-heart',
  technical_support: 'lifebuoy',
  sales_order_taking: 'scales',
  retention: 'fire-extinguisher',
  healthcare_patient_services: 'first-aid',
  account_management: 'user-square',
}

const PROFILE_OPTIONS: CallProfile[] = Object.keys(CALL_PROFILE_LABELS) as CallProfile[]

function RouteComponent() {
  const navigate = useNavigate()
  const { update, isPending: isSaving } = OrganizationMutators.useUpdate({
    onSuccess: () => {
      navigate({ to: '/$alias', from: Route.fullPath })
    },
  })
  const [selectedProfiles, setSelectedProfiles] = useState<Set<CallProfile>>(new Set())
  const hasSelection = selectedProfiles.size > 0

  const toggleProfile = (profile: CallProfile) => {
    setSelectedProfiles((prev) => {
      const next = new Set(prev)
      if (next.has(profile)) next.delete(profile)
      else next.add(profile)
      return next
    })
  }

  const handleContinue = () => {
    update({ setup: { callProfiles: Array.from(selectedProfiles) } })
  }

  return (
    <SetupWizard title="Organization Setup" currentStep={3}>
      <div className="border-background flex w-full shrink grow flex-col items-center justify-center border-b-4 p-8">
        <header className="mb-10 text-center">
          <Heading1>Tell Us About Your Team</Heading1>
          <p>What kind of calls are we working with?</p>
        </header>
        <div className="grid max-w-prose grid-cols-3 gap-2">
          {PROFILE_OPTIONS.map((profile) => (
            <label
              onClick={() => toggleProfile(profile)}
              key={profile}
              className={cn(
                'shadow-hard bg-background border-foreground/50 text-foreground flex aspect-square cursor-pointer flex-col items-center justify-center gap-4 rounded-sm border text-center select-none',
                {
                  'border-primary text-primary outline-lime-green translate-y-[2px] shadow-none':
                    selectedProfiles.has(profile),
                },
              )}
            >
              <div className="flex shrink grow flex-col items-center justify-center gap-6 p-4">
                <div
                  className={cn(
                    'bg-accent block flex size-18 items-center justify-center rounded-full',
                    {
                      'bg-lime-green': selectedProfiles.has(profile),
                    },
                  )}
                >
                  <Icon
                    name={PROFILE_ICONS[profile]}
                    weight={selectedProfiles.has(profile) ? 'light' : 'light'}
                    className={cn('fill-foreground/60 size-10', {
                      'fill-primary': selectedProfiles.has(profile),
                    })}
                  />
                </div>
                <span className="font-medium">{CALL_PROFILE_LABELS[profile]}</span>
              </div>
            </label>
          ))}
        </div>
        <Button
          onClick={handleContinue}
          disabled={!hasSelection || isSaving}
          className="mt-10 w-full max-w-prose"
          variant="lime"
        >
          {isSaving ? 'Saving...' : 'Continue'}
        </Button>
      </div>
    </SetupWizard>
  )
}
